/* eslint-disable react-hooks/exhaustive-deps */
import { Modal } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { DEFAULT_REGION_BANK_HEADER_VALUE, DELAY } from '@/constants'
import { formatProducts } from '@/pages/Cart/Cart.config'
import { useCartStore } from '@/stores/cart/cartStore'
import { useOrderByCodeStore } from '@/stores/cart/orderByCodeStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  CartProductInterface,
  FeedProductInterface,
  OrderProductInterface,
  OrderProductModel,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import getProductKey from '@/utils/products/getProductKey'

import cn from './QuantityToggler.module.scss'

export type QuantityTogglerPropsType = {
  location:
    | 'addToCartModal'
    | 'cart'
    | 'profile/orders/:code'
    | 'orderSelection'
  element:
    | CartProductInterface
    | FeedProductInterface
    | OrderProductInterface
    | OrderProductModel
  productQuantity?: number
  orderCode?: number
  maxQuantity?: number
  onEnter?: () => void
  itemDesc?: string
  updateProducts?: (newProducts: CartProductInterface[]) => void
  isShowWarning?: boolean
  onQuantityChange?: (val: number) => void
}

function QuantityToggler(props: QuantityTogglerPropsType) {
  const {
    location,
    element,
    productQuantity,
    orderCode,
    onEnter,
    maxQuantity,
    itemDesc,
    updateProducts,
    isShowWarning = false,
    onQuantityChange,
  } = props

  const getInitialQuantity = () => {
    if (location === 'orderSelection') {
      return productQuantity ?? 1
    }

    if (location == 'cart') {
      if ('suppliersStock' in element) {
        const stock = element.suppliersStock[0].stock[0]
        return stock.cartInfo?.quantity ?? stock.quantityInCart ?? 0
      }
    } else if (location == 'profile/orders/:code') {
      const prod = element as OrderProductModel
      return prod.quantity
    }
    return 0
  }

  const [quantity, setQuantity] = useState(() => {
    if (location === 'addToCartModal') return ''
    return String(getInitialQuantity())
  })

  useEffect(() => {
    if (productQuantity !== undefined) {
      setQuantity(String(productQuantity))
      lastSentValueRef.current = productQuantity
    }
  }, [productQuantity])

  useEffect(() => {
    if (location === 'orderSelection' && productQuantity !== undefined) {
      setQuantity(String(productQuantity))
    }
  }, [productQuantity, location])
  const inputRef = useRef<HTMLInputElement>(null)

  const lastSentValueRef = useRef<number | null>(
    location !== 'addToCartModal' ? getInitialQuantity() : null
  )

  const { setDuplicateProductsQuantities } = useCartStore((state) => state)
  const {
    duplicateOrderProductsQuantities,
    setDuplicateOrderProductsQuantities,
  } = useOrderByCodeStore((state) => state)
  const setModalProductQuantity = useFeedStore(
    (state) => state.setModalProductQuantity
  )
  const { currentClient, userOrganisation } = useUserInfoStore((state) => state)
  const [modal, contextHolder] = Modal.useModal()

  const getAvailableQuantity = () => {
    if (maxQuantity !== undefined) {
      return maxQuantity
    }

    if (location === 'profile/orders/:code') {
      return (element as OrderProductModel).availableQuantity ?? 0
    }

    if ('suppliersStock' in element) {
      return element.suppliersStock[0].stock[0].quantity ?? 0
    }

    return 0
  }

  const availableQuantity = getAvailableQuantity()

  useEffect(() => {
    if (location === 'cart' && 'suppliersStock' in element) {
      const stockItem = element.suppliersStock[0].stock[0]
      const newAvailableQuantity =
        stockItem.cartInfo?.quantity ?? stockItem.quantityInCart ?? 0
      const currentQuantityInCart = Number(quantity)

      if (
        currentQuantityInCart === 0 ||
        (currentQuantityInCart > newAvailableQuantity &&
          newAvailableQuantity > 0) ||
        (currentQuantityInCart !== newAvailableQuantity &&
          newAvailableQuantity > 0)
      ) {
        setQuantity(String(newAvailableQuantity))
        lastSentValueRef.current = newAvailableQuantity
      }
    }
  }, [element, location])

  useEffect(() => {
    if (location === 'addToCartModal') {
      inputRef.current?.focus()
      if (productQuantity) return
      setQuantity('')
      setModalProductQuantity(0)
      lastSentValueRef.current = null
    }
  }, [location])

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const updateQuantity = (rawValue: number | '') => {
    if (rawValue === '') {
      setQuantity('')
      if (location === 'addToCartModal') setModalProductQuantity(0)

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
      return
    }

    let normalizedQuantity: number

    if (availableQuantity === 0 && location === 'cart') {
      normalizedQuantity = Math.max(
        1,
        Math.min(rawValue, Number(quantity) || 1)
      )
    } else {
      normalizedQuantity = Math.max(1, Math.min(rawValue, availableQuantity))
    }

    setQuantity(String(normalizedQuantity))

    if (location === 'addToCartModal') {
      setModalProductQuantity(normalizedQuantity)
      return
    }

    if (location === 'orderSelection') {
      onQuantityChange?.(normalizedQuantity)
      return
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(() => {
      if (lastSentValueRef.current === normalizedQuantity) {
        return
      }

      const prevQuantity = lastSentValueRef.current
      lastSentValueRef.current = normalizedQuantity

      if (location === 'cart' && 'id' in element) {
        const cartItem = element as CartProductInterface

        let clientId: string | null = null
        if (currentClient?.id) {
          clientId = currentClient.id
        } else if (userOrganisation?.id) {
          clientId = userOrganisation.id
        }

        const loadingToast = toast.loading('Обновление количества...')

        api
          .patch(
            `/cart/items/${cartItem.id}`,
            { quantity: normalizedQuantity },
            {
              headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
              params: { client: clientId },
            }
          )
          .then((response) => {
            toast.dismiss(loadingToast)

            const locationHeader =
              response.headers['location'] || response.headers['Location']
            let newCartItemId: string | null = null
            if (locationHeader) {
              newCartItemId = locationHeader.substring(
                locationHeader.lastIndexOf('/') + 1
              )
            }

            const rawContent = response.data.cart?.cartItems?.content

            if (rawContent) {
              const mapped = formatProducts(rawContent)
              updateProducts?.(mapped)

              const productKey = getProductKey(element)
              const updatedProduct = mapped.find(
                (p) => String(getProductKey(p)) === String(productKey)
              )

              if (updatedProduct) {
                const latestStock =
                  updatedProduct.suppliersStock?.[0]?.stock?.[0]
                const latestInCart =
                  latestStock?.cartInfo?.quantity ??
                  latestStock?.quantityInCart ??
                  0
                setDuplicateProductsQuantities(
                  String(getProductKey(updatedProduct)),
                  latestInCart
                )
                lastSentValueRef.current = latestInCart
              }
            } else {
              const stockItem = element.suppliersStock[0].stock[0]

              setDuplicateProductsQuantities(
                String(getProductKey(element)),
                normalizedQuantity
              )

              if (newCartItemId && stockItem.cartInfo) {
                stockItem.cartInfo.id = newCartItemId
              }
            }
          })
          .catch((e) => {
            toast.dismiss(loadingToast)
            lastSentValueRef.current = null
            handleApiError(e, modal, {
              description: cartItem.nomenclatureDto?.description,
              article: cartItem.nomenclatureDto?.article,
              brandName: cartItem.nomenclatureDto?.brandDto?.description,
              storeName:
                cartItem.suppliersStock?.[0]?.stock?.[0]?.storeShortModel
                  ?.description,
            })
          })
      }

      if (location === 'profile/orders/:code') {
        const prod = element as OrderProductModel
        const store = prod.storeShortModel
        const route = prod.route ?? []

        setDuplicateOrderProductsQuantities({
          ...duplicateOrderProductsQuantities,
          [String(getProductKey(prod))]: normalizedQuantity,
        })

        const buildKey = () => ({
          supplierId: store?.supplierId,
          productCode: prod.nomenclatureDto.code,
          storeCode: store?.code,
        })

        api
          .patch(
            `/orders/${orderCode}/product-item`,
            {
              key: buildKey(),
              route,
              quantityToSet: normalizedQuantity,
            },
            { headers: { 'Region-bank': DEFAULT_REGION_BANK_HEADER_VALUE } }
          )
          .then(() => {
            toast.success('Количество обновлено', {
              description: `Установлено: ${normalizedQuantity} шт.`,
            })
          })
          .catch((e) => {
            const errorData = e.response?.data
            lastSentValueRef.current = null

            const detailsMessage = errorData?.details?.[0]?.message
            const mainMessage = errorData?.message
            const traceId = errorData?.traceId

            const prev = prevQuantity ?? 1
            setQuantity(String(prev))
            setDuplicateOrderProductsQuantities({
              ...duplicateOrderProductsQuantities,
              [String(getProductKey(prod))]: prev,
            })

            toast.error('Ошибка обновления количества', {
              description: [
                detailsMessage || mainMessage,
                traceId ? `traceId: ${traceId}` : null,
              ]
                .filter(Boolean)
                .join('\n'),
            })
          })
      }
    }, DELAY)
  }

  const handleDecrement = () => {
    updateQuantity(Number(quantity) - 1)
  }
  const handleIncrement = () => {
    updateQuantity(Number(quantity) + 1)
  }

  const shouldShowWarning =
    Number(quantity) >= availableQuantity && availableQuantity > 0

  const isArchived =
    location === 'cart' &&
    'availabilityStatus' in element &&
    element.availabilityStatus === 'PRICE_UNAVAILABLE'

  const shouldShowNoStockWarning =
    !isArchived && availableQuantity === 0 && Number(quantity) > 0

  const isIncrementDisabled =
    isArchived ||
    availableQuantity === 0 ||
    Number(quantity) >= availableQuantity

  return (
    <>
      {contextHolder}
      {isShowWarning && shouldShowWarning ? (
        <div className={cn.availableWarning}>
          Доступно {availableQuantity === Infinity ? '∞' : availableQuantity}{' '}
          {itemDesc}
        </div>
      ) : null}

      {isShowWarning && shouldShowNoStockWarning ? (
        <div className={cn.noStockText}>Нет на складе</div>
      ) : null}

      <div
        className={
          location === 'cart'
            ? [cn.container, cn.inCart].join(' ')
            : cn.container
        }
      >
        <button
          className={cn.sign}
          disabled={isArchived || !quantity || Number(quantity) <= 1}
          onClick={handleDecrement}
        >
          -
        </button>

        <input
          id={`quantity-${getProductKey(element)}`}
          name={`quantity-${getProductKey(element)}`}
          ref={inputRef}
          className={cn.input}
          type="text"
          inputMode="numeric"
          value={quantity}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '')
            updateQuantity(val === '' ? '' : Number(val))
          }}
          onBlur={(e) => {
            const val = e.target.value.trim()
            if (val === '') {
              if (location === 'cart') {
                updateQuantity(1)
              } else {
                setQuantity('')
              }
            } else {
              setQuantity(val)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && location === 'addToCartModal') {
              const val = Number(e.currentTarget.value)
              if (val > 0) {
                updateQuantity(val)
                onEnter?.()
              }
            }
          }}
        />

        <button
          className={cn.sign}
          disabled={isIncrementDisabled}
          onClick={handleIncrement}
        >
          +
        </button>
      </div>
    </>
  )
}

export default QuantityToggler
